import { motion } from "framer-motion";
import { Phone, MessageCircle, MapPin, Navigation, Download } from "lucide-react";
import { BUSINESS, downloadVCard } from "@/lib/siteData";

const ContactSection = () => (
    <section id="contact" data-testid="contact-section" className="py-16 sm:py-24 bg-[#1C2541]/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
            <motion.p
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="text-xs font-mono2 uppercase tracking-[0.3em] text-amber-400/90"
            >
                Get In Touch
            </motion.p>
            <motion.h2
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="font-display mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-50"
            >
                Talk to us about your next property
            </motion.h2>

            <div className="mt-10 grid gap-5 lg:grid-cols-2">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="card-glow rounded-2xl border border-amber-500/20 bg-[#16203B] p-6 sm:p-8"
                >
                    <div className="flex items-start gap-4">
                        <div className="w-11 h-11 rounded-xl bg-amber-400/10 border border-amber-500/30 flex items-center justify-center shrink-0">
                            <MapPin size={20} className="text-amber-400" />
                        </div>
                        <div>
                            <h3 className="font-display text-xl font-semibold text-slate-100">
                                Visit Our Office
                            </h3>
                            <address
                                data-testid="office-address-text"
                                className="mt-2 not-italic text-sm leading-relaxed text-slate-300"
                            >
                                {BUSINESS.addressLines.map((line) => (
                                    <span key={line} className="block">
                                        {line}
                                    </span>
                                ))}
                            </address>
                            <a
                                href={BUSINESS.mapsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="google-maps-direction-link"
                                aria-label="Get directions to Shubham Housing office on Google Maps"
                                className="mt-4 inline-flex items-center gap-2 rounded-full border border-amber-500/50 text-amber-300 px-5 py-2.5 text-sm font-bold hover:bg-amber-400/10 transition-colors"
                            >
                                <Navigation size={15} /> Get Directions
                            </a>
                        </div>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.12 }}
                    data-testid="vcard-preview-box"
                    className="card-glow rounded-2xl border border-amber-500/20 bg-[#16203B] p-6 sm:p-8"
                >
                    <h3 className="font-display text-xl font-semibold text-slate-100">
                        Save Our Contact
                    </h3>
                    <p className="mt-2 text-sm text-slate-400">
                        Add {BUSINESS.director} ({BUSINESS.role}, Shubham Housing) directly to your
                        phone contacts.
                    </p>
                    <div className="mt-5 space-y-3">
                        <a
                            href={BUSINESS.tel1}
                            data-testid="vcard-phone-1-link"
                            aria-label={`Call ${BUSINESS.phone1Display}`}
                            className="flex items-center gap-3 rounded-xl border border-amber-500/20 bg-[#0B132B]/60 px-4 py-3 hover:border-amber-500/50 transition-colors"
                        >
                            <Phone size={17} className="text-amber-400" />
                            <span className="text-sm font-semibold text-slate-100">
                                {BUSINESS.phone1Display}
                            </span>
                        </a>
                        <a
                            href={BUSINESS.tel2}
                            data-testid="vcard-phone-2-link"
                            aria-label={`Call ${BUSINESS.phone2Display}`}
                            className="flex items-center gap-3 rounded-xl border border-amber-500/20 bg-[#0B132B]/60 px-4 py-3 hover:border-amber-500/50 transition-colors"
                        >
                            <Phone size={17} className="text-amber-400" />
                            <span className="text-sm font-semibold text-slate-100">
                                {BUSINESS.phone2Display}
                            </span>
                        </a>
                        <a
                            href={BUSINESS.whatsapp}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Chat on WhatsApp"
                            className="flex items-center gap-3 rounded-xl border border-emerald-400/30 bg-[#0B132B]/60 px-4 py-3 hover:border-emerald-400/60 transition-colors"
                        >
                            <MessageCircle size={17} className="text-[#25D366]" />
                            <span className="text-sm font-semibold text-slate-100">
                                WhatsApp — {BUSINESS.phone1Display}
                            </span>
                        </a>
                    </div>
                    <button
                        onClick={downloadVCard}
                        data-testid="vcard-download-button"
                        aria-label="Download contact card to save on your phone"
                        className="mt-5 inline-flex items-center gap-2 rounded-full bg-amber-400 text-[#0B132B] px-5 py-2.5 text-sm font-bold hover:bg-amber-300 transition-colors"
                    >
                        <Download size={15} /> Save Contact (.vcf)
                    </button>
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.15 }}
                className="mt-5 rounded-2xl border border-amber-500/20 bg-[#16203B] p-2 overflow-hidden"
            >
                <iframe
                    data-testid="google-map-embed"
                    title="Shubham Housing office location — Tower 4, DLF Corporate Green, Sector 74A, Gurugram"
                    src="https://www.google.com/maps?q=Tower+4,+DLF+Corporate+Green,+Sector+74A,+Gurugram,+Haryana+122004&output=embed"
                    className="w-full h-72 sm:h-96 rounded-xl border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                />
            </motion.div>
        </div>
    </section>
);

export default ContactSection;
